import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Loader2, Crown, Sparkles } from "lucide-react";
import DOMPurify from "dompurify";
import { api } from "../lib/api";
import { usePageDescription } from "../lib/usePageDescription";
import PageHero from "../components/PageHero";

const EventCard = ({ e }) => {
  const firstBadge = (e.badges || [])[0];
  const headerImage = e.news_header || "";
  return (
    <Link
      to={`/events/${e.id}`}
      data-testid={`event-card-${e.id}`}
      className={`card-hover block rounded-xl border overflow-hidden ${e.is_legendary ? "border-[#ffb84d]/40 bg-gradient-to-br from-[#1a1410] via-[#0f141b] to-[#0f141b] ring-1 ring-[#ffb84d]/15" : "border-white/10 bg-[#0f141b]"}`}
    >
      <div className="relative flex items-center justify-center aspect-[16/9] overflow-hidden bg-gradient-to-br from-[#1a1f29] to-[#10141b]">
        {headerImage ? (
          <img src={headerImage} alt="" className="hb-pixel-img h-full w-full object-cover" />
        ) : e.image ? (
          <img src={e.image} alt={e.name} className="hb-pixel-img max-h-24" />
        ) : firstBadge ? (
          <img src={`https://images.habbo.com/c_images/album1584/${firstBadge.code}.gif`} alt={firstBadge.code} className="hb-pixel-img h-20 w-20" />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-xl bg-[#10141b] border border-white/5">
            <Calendar className="h-9 w-9 text-[#ffb84d]" />
          </div>
        )}
        {/* Legendaer-Marker: Badge wenn da, sonst Krone */}
        {e.is_legendary && (
          <div className="absolute right-2 top-2" data-testid={`event-legendary-marker-${e.id}`}>
            {firstBadge ? (
              <div className="flex items-center gap-1 rounded-full border border-[#ffb84d]/40 bg-black/50 px-1.5 py-1 backdrop-blur">
                <img src={`https://images.habbo.com/c_images/album1584/${firstBadge.code}.gif`} alt="legendary" className="hb-pixel-img h-6 w-6" />
              </div>
            ) : (
              <div className="flex h-7 w-7 items-center justify-center rounded-full border border-[#ffb84d]/40 bg-black/50 backdrop-blur">
                <Crown className="h-3.5 w-3.5 text-[#ffb84d]" />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="text-xs text-[#9d7bff]">{e.year}</div>
        <h3 className="mt-1 text-lg font-semibold text-white">{e.name}</h3>
        <p className="mt-2 text-sm text-zinc-400 line-clamp-2">{e.short}</p>

        {e.description_html && (
          <div
            className="news-content mt-3 max-w-none text-xs leading-6 text-zinc-300 line-clamp-3"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(e.description_html) }}
          />
        )}

        {e.badges && e.badges.length > 0 && (
          <div className="mt-4 border-t border-white/5 pt-3">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[#b39bff]">Verspielte Badges</div>
            <div className="flex flex-wrap gap-2">
              {e.badges.slice(0, 4).map((b) => (
                <img key={b.code} src={`https://images.habbo.com/c_images/album1584/${b.code}.gif`} alt={b.code} title={b.name} className="hb-pixel-img h-7 w-7" />
              ))}
              {e.badges.length > 4 && <span className="self-center text-[10px] text-zinc-500">+{e.badges.length - 4}</span>}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const pd = usePageDescription("events", {
    title: "Habbo Events",
    intro: "Allgemeine Habbo-Events + die legendaeren Highlights aus 20+ Jahren – mit Badges, die ihr verspielen konntet.",
  });

  useEffect(() => {
    (async () => {
      try { setEvents(await api.listEvents()); }
      finally { setLoading(false); }
    })();
  }, []);

  const legendary = useMemo(() => events.filter((e) => e.is_legendary), [events]);
  const general = useMemo(() => events.filter((e) => !e.is_legendary), [events]);

  const visible = filter === "legendary" ? legendary : filter === "general" ? general : events;

  return (
    <section className="mx-auto max-w-7xl px-4 py-10" data-testid="events-page">
      <PageHero eyebrow={pd.eyebrow || "Höhepunkte"} icon={Sparkles} title={pd.title} keywords={pd.keywords} intro={pd.intro} />

      {/* Filter */}
      <div className="mt-6 inline-flex rounded-lg border border-white/10 bg-[#0f141b] p-1">
        {[
          { id: "all", label: `Alle (${events.length})` },
          { id: "legendary", label: `★ Exklusiv (${legendary.length})` },
          { id: "general", label: `Allgemein (${general.length})` },
        ].map((opt) => (
          <button
            key={opt.id}
            onClick={() => setFilter(opt.id)}
            data-testid={`events-filter-${opt.id}`}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${filter === opt.id ? "bg-[#7c4dff] text-white" : "text-zinc-400 hover:text-zinc-200"}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <>
          {filter === "all" && legendary.length > 0 && (
            <section className="mt-8">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#ffb84d]">
                <Crown className="h-4 w-4" /> Exklusive Events
              </h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {legendary.map((e) => <EventCard key={e.id} e={e} />)}
              </div>
            </section>
          )}
          {filter === "all" && general.length > 0 && (
            <section className="mt-10">
              <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-zinc-300">Allgemeine Events</h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {general.map((e) => <EventCard key={e.id} e={e} />)}
              </div>
            </section>
          )}
          {filter !== "all" && (
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((e) => <EventCard key={e.id} e={e} />)}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default Events;
